import { MutableRefObject, useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { MaiChatEvents } from '~/analytics/maiChatEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGetMaiPromptsQuery } from '~/store/services/mai';

import { MAI_SUGGESTED_PROMPTS } from './maiChat.constants';
import { MaiSuggestedPrompt } from './maiChat.types';

const NO_PROMPTS: ReadonlyArray<MaiSuggestedPrompt> = [];

export const useMaiSuggestedPrompts = (
  patientId: string,
  conversationIdRef: MutableRefObject<string>,
): ReadonlyArray<MaiSuggestedPrompt> => {
  const { maiPromptsEndpoint } = useFlags<LDFlags>();
  const analytics = useAnalytics();
  const { data, error } = useGetMaiPromptsQuery(
    { patientId },
    { skip: !maiPromptsEndpoint },
  );

  // While RTK refetches a cached failure, isError is false but error stays set.
  // A remount starts from that cached error, which the earlier mount reported.
  const inheritedErrorRef = useRef(error);
  const failureTrackedRef = useRef(false);
  useEffect(() => {
    if (
      !error ||
      error === inheritedErrorRef.current ||
      failureTrackedRef.current
    ) {
      return;
    }
    failureTrackedRef.current = true;
    analytics?.trackEvent(MaiChatEvents.promptsLoadFailed, {
      patientId,
      'Conversation Id': conversationIdRef.current,
    });
  }, [analytics, conversationIdRef, error, patientId]);

  if (!maiPromptsEndpoint || error) return MAI_SUGGESTED_PROMPTS;
  return data?.prompts ?? NO_PROMPTS;
};
